/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cx from 'classnames';

import './AppMenu.scss';
import { UnselectedProfileAvatar, SelectedProfileAvatar } from '../../components/ProfileAvatar';
import { getActiveProfile, getMainProfileId, getProfiles } from '../redux/reduxSelectors';
import IconProfile from '../../components/icons/IconProfile';
import IconGroups from '../../components/icons/IconGroups';
import IconShare from '../../components/icons/IconShare';
import IconAdd from '../../components/icons/IconAdd';
import R from '../../appAuthorized/RouteNames';
import { navigateReplace } from '../redux/NavigationActions';
import { selectors as colorThemeSelector } from '../../redux/ducks/lists/colorTheme';
import DateUtil from '../../../utilities/DateUtil';
import { selectors as sessionSelectors } from '../../redux/ducks/session';
import { selectors as uiSelectors } from '../../redux/ducks/ui';
import ProfileModel from '../../../models/ProfileModel';
import { IconAccess, IconBack, IconForward } from '../../components/icons/SvgIcons';
import AppUtils from '../../../utilities/AppUtils';
import { appManifest } from '../../../utilities/appManifest';
import IconHealthInfo from '../../components/icons/IconHealthInfo';
import IconSettings from '../../components/icons/IconSettings';
import IconDataConnections from '../../components/icons/IconDataConnections';
import LegalDocumentLinks from '../../components/LegalDocumentLinks';
import { TrackEvent } from '../../../utilities/UserMetrics';

import { Icons } from '../../../bphComponents/bundle';
import { SmallSecondaryButton } from '../../components/buttons/Buttons';
import FlexRow from '../../components/FlexRow';
import { ExternalOrInBrowserlLink } from '../../components/TextLink';
import { openInAppBrowser } from '../../../utilities/MobileUtil';
import { useNavigate } from '../../hooks/useNavigate';
import { useUIState } from '../../hooks/useUIState';
import { useActiveUser } from '../../hooks/useUser';

interface Props {
  history: any;
  location: any;
  avatarSize: number;
  activeProfile: ProfileModel;
  profiles: ProfileModel[];
  profileList: any[];
  colorTheme: any;
  showUpgradeButton: boolean;
  appMenuStateName: string;
  isSectionVisible: boolean;
}

const SCREEN_WIDTH_LIMIT = 834;

export const BasicAppMenu: React.FC<Props> = (props) => {
  const { location, activeProfile, profiles, profileList, history, avatarSize, colorTheme, showUpgradeButton, appMenuStateName, isSectionVisible } = props;

  const [, uiStateActions] = useUIState();
  const [, navActions] = useNavigate();
  const [{ activeProfileId, isActiveProfileMainProfile }, uiActions] = useUIState();
  const [{ user }] = useActiveUser();
  const mainProfileId = user.profile_id;

  if (!activeProfile) return null;

  const closeSection = () => {
    uiStateActions.closeAppMenuSection();
  };

  const closeAppMenu = () => {
    uiStateActions.closeAppMenuSection();
    uiStateActions.hideAppMenu();
  };

  return (
    <div className={cx('cmp-app-menu profile-background-color-accent font-color-white', { section: isSectionVisible })}>

      <MainMenu
        activeProfile={activeProfile}
        history={history}
        profileList={profileList}
        selectProfile={(profile) => {
          uiActions.setActiveProfile(profile.data.id);
        }}
        avatarSize={avatarSize}
        handleAvatarPanelClick={() => {
          const { innerWidth: screenWidth } = window;

          if (screenWidth >= SCREEN_WIDTH_LIMIT) {
            navActions.goTo(R.home());
          } else {
            navActions.replace(location.pathname);
          }
        }}
        logout={() => {
          AppUtils.logout({}, AppUtils.reloadApp);
        }}
        openProfile={() => {
          uiStateActions.showAppMenuProfileSection();
        }}
        openHelp={() => {
          uiStateActions.showAppMenuHelpSection();
        }}
        isMainProfile={isActiveProfileMainProfile}
        colorTheme={colorTheme}
        closeAppMenu={closeAppMenu}
      />

      {appMenuStateName === 'help' &&
        <HelpSection
          back={closeSection}
          history={history}
          showUpgradeButton={showUpgradeButton}
          closeMenu={closeAppMenu}
        />
      }

      {appMenuStateName === 'profile' &&
        <ProfilesSection
          visible={isSectionVisible}
          back={closeSection}
          profiles={profiles}
          activeProfile={activeProfile}
          selectProfile={(profile) => {
            uiActions.setActiveProfile(profile.data.id);
            closeSection();
          }}
          activeProfileId={activeProfileId}
          mainProfileId={mainProfileId}
          closeMenu={closeAppMenu}
        />
      }
    </div>
  );
};

export const AppMenu = withRouter(connect(mapStateToProps, mapDispatchToProps)(BasicAppMenu));

const MainMenu = ({ activeProfile, profileList, history, selectProfile, avatarSize, handleAvatarPanelClick, logout, openProfile, openHelp, isMainProfile, colorTheme, closeAppMenu }) => (
  <div className="__mainmenu">
    <div className="avatar-panel">
      <div className="click-wrapper" onClick={handleAvatarPanelClick}>
        <div className="icon">
          <UnselectedProfileAvatar
            profile={activeProfile}
            size={avatarSize}
            imgClassName="border-color-profile-highlight"
          />
        </div>

        <div className="label">
          <FormattedMessage
            id={isMainProfile ? 'appNav.menu.user.heading.text' : 'appNav.menu.notUser.heading.text'}
            values={{ string1: activeProfile.greetingName() }}
          />
        </div>
      </div>
    </div>

    <NavPanel
      history={history}
      closeMenu={closeAppMenu}
    />

    <ProfilesPanel
      activeProfile={activeProfile}
      profileList={profileList}
      selectProfile={selectProfile}
      openSection={openProfile}
      colorTheme={colorTheme}
      closeMenu={closeAppMenu}
    />

    <div
      className={cx('action-panel', 'profile-border-bottom-color-highlight-25', { current: history.location.pathname.indexOf(R.accountSettings()) >= 0 })}
    >
      <div className="click-wrapper" onClick={() => { navigateReplace(R.accountSettings()); closeAppMenu(); }}>
        <div className="current-indicator profile-background-color"/>

        <div className="label">
          <FormattedMessage id="appMenu.settings.button.label"/>
        </div>
        <div className="icon" style={{ position: 'relative', left: -2 }}>
          <IconSettings.Medium color="white" />
        </div>
      </div>
    </div>

    <div
      className={cx('action-panel', 'profile-border-bottom-color-highlight-25', { current: history.location.pathname.indexOf(R.contactUs()) >= 0 })}
    >
      <div className="click-wrapper" onClick={openHelp}>
        <div className="current-indicator profile-background-color"/>

        <div className="label">
          <FormattedMessage id="appMenu.help.button.label"/>
        </div>
        <div className="icon">
          <IconForward/>
        </div>
      </div>
    </div>

    <div className="action-panel logout">
      <div className="click-wrapper" onClick={logout}>
        <div className="label">
          <FormattedMessage id="list.appMenu.primary.logout.name"/>
        </div>
      </div>
    </div>
  </div>
);

const HelpSection = ({ back, history, showUpgradeButton, closeMenu }) => (
  <div className="section profile-background-color-accent">
    <div className="content" style={{ minHeight: 'inherit' }}>
      <div className="icon-back" onClick={back}>
        <IconBack/>
      </div>

      <div className="title">
        <FormattedMessage id="appMenu.help.heading.text"/>
      </div>

      <FormattedMessage id="list.appMenu.primary.help.url">
        {
          (url: string) => (
            <a className="item" href={url} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="appMenu.helpCenter.button.label"/>
            </a>
          )
        }
      </FormattedMessage>

      <div
        className={cx('item', { current: history.location.pathname.indexOf(R.contactUs()) >= 0 })}
        onClick={() => { navigateReplace(R.contactUs()); closeMenu(); }}
      >
        <div className="current-indicator profile-background-color"/>

        <FormattedMessage id="appMenu.contact.button.label"/>
      </div>
    </div>

    <div className="footer">
      <div className={cx('separator', 'profile-border-bottom-color-highlight-25')}/>

      <FlexRow className="item version">
        <FormattedMessage id="app.releaseNotes.url">
          {
            (url: string) => (
              <ExternalOrInBrowserlLink url={url} className="font-color-gsLightest font-footnote">
                <FormattedMessage
                  id="appMenu.appVersion.label"
                  values={{ string1: appManifest.app_version }}
                />
              </ExternalOrInBrowserlLink>
            )
          }
        </FormattedMessage>

        {
          showUpgradeButton ? (
            <SmallSecondaryButton
              onClick={() => { openInAppBrowser(AppUtils.googlePlayAppLink()); }}
              className="border-color-gsLightest font-color-gsLightest"
            >
              <FormattedMessage id="appMenu.updateApp.button.label" />
            </SmallSecondaryButton>
          ) : null
        }

      </FlexRow>
      {(AppUtils.isMobileDevice() && !IS_ANDROID_BUILD) && (
        <a className="item" href={AppUtils.appLink()} target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            id="appMenu.appDownload.button.label"
          />
        </a>
      )}
      <div className={cx('separator', 'profile-border-bottom-color-highlight-25')}/>

      <LegalDocumentLinks.PrivacyPolicy className="item">
        <FormattedMessage id="appMenu.privacy.button.label" />
      </LegalDocumentLinks.PrivacyPolicy>

      <LegalDocumentLinks.TermsOfService className="item">
        <FormattedMessage id="appMenu.terms.button.label" />
      </LegalDocumentLinks.TermsOfService>

      <LegalDocumentLinks.Acknowledgements className="item">
        <FormattedMessage id="appMenu.about.button.label"/>
      </LegalDocumentLinks.Acknowledgements>

      <FlexRow className="item">
        <FormattedMessage id="legal.ccpaForm.URL">
          {
            (url: string) => (
              <ExternalOrInBrowserlLink url={url} className="font-color-gsLightest font-footnote">
                <FormattedMessage id="appMenu.ccpaForm.button.label"/>
              </ExternalOrInBrowserlLink>
            )
          }
        </FormattedMessage>
      </FlexRow>

      <div className="item copy">
        <FormattedMessage id="backpack.copyright.message.text"/>
      </div>
    </div>
  </div>
);

const NavPanel = ({ history, closeMenu }) => {
  const items = [
    ...navItems,
  ];

  const { idx: activeIdx } = items
    .map(({ href }, idx) => ({ href, idx }))
    .sort((a, b) => b.href.length - a.href.length)
    .find(({ href }) => history.location.pathname.indexOf(href) === 0)
      || { idx: - 1 };

  return (
    <div className={cx('nav-panel', 'profile-border-bottom-color-highlight-25')}>
      {items.map(({ icon, label, onClick }, idx) => (
        <div
          key={label}
          className={cx('nav-item', { current: idx === activeIdx })}
          onClick={() => { onClick(); closeMenu(); }}
        >
          <div className={cx('icon', { 'profile-background-color': idx === activeIdx })}>{icon}</div>
          <div className="label">
            <FormattedMessage id={label}/>
          </div>
        </div>
      ))}
    </div>
  );
};

function onClickAvatar(e, pData, selectProfile) {
  e.stopPropagation();
  selectProfile(new ProfileModel(pData));
  navigateReplace(R.myBackpack());
}

const ProfilesPanel = ({ activeProfile, profileList, selectProfile, openSection, colorTheme, closeMenu }) => {
  const [, uiActions] = useUIState();
  const sortedProfiles = uiActions.sortLastActiveProfiles(profileList);
  const isAddMode = sortedProfiles.length <= 1;
  const className = cx('action-panel', 'profile-border-bottom-color-highlight-25', 'profiles-panel', { 'is-add-mode': isAddMode });
  const hasManyProfiles = sortedProfiles.length > 3;
  const { r, g, b } = colorTheme.accent_color;

  return (
    <div className={className}>
      <div
        className="click-wrapper"
        onClick={() => {
          if (!isAddMode) { return; }
          navigateReplace(R.createDependentProfile());
          closeMenu();
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: 160 }}>
          <div>
            {sortedProfiles.length > 1
              ? <FormattedMessage id="appMenu.profileSwitch.label.text"/>
              : <FormattedMessage id="appMenu.addProfile.button.label"/>
            }
          </div>

          {sortedProfiles.filter(p => p.id !== activeProfile.data.id).slice(0, 2).map(p => (
            <div
              key={p.id}
              onClick={(e) => {
                onClickAvatar(e, p, selectProfile);
                closeMenu();
              }}
              style={{ marginLeft: 10, cursor: 'pointer' }}
            >
              <UnselectedProfileAvatar
                profile={new ProfileModel(p)}
                altText="appMenu.profileSwitch.me.button.hint"
              />
            </div>
          ))}
        </div>
        {hasManyProfiles && <div className="fade" style={{ background: `linear-gradient(to left, rgba(${r}, ${g}, ${b}, 1) 23%, rgba(${r}, ${g}, ${b}, 0) 100%)` }} /> }
        <div
          className="icon profile-background-color-accent"
          onClick={() => {
            if (hasManyProfiles) {
              openSection();
            } else {
              navigateReplace(R.createDependentProfile());
              closeMenu();
            }
          }}
          style={{ right: hasManyProfiles ? 4 : 14 }}
        >
          {hasManyProfiles
            ? <IconForward/>
            : <IconAdd fillClassName="fill-color-transparent" strokeClassName="stroke-color-white" size="26"/>
          }
        </div>
      </div>
    </div>
  );
};

interface ProfilesSectionProps {
  visible: boolean;
  back(): void;
  profiles: any[];
  activeProfile: any;
  selectProfile(p, c): void;
  activeProfileId: number;
  closeMenu(): void;
  mainProfileId: number;
}
const ProfilesSection: React.FC<ProfilesSectionProps> = (props) => {
  const { visible, back, profiles, activeProfile, selectProfile, activeProfileId, closeMenu, mainProfileId } = props;

  const scrollerRef = React.useRef(null);

  const updateScrollPosition = () => {
    // NOTE, simple querySelector('.me').scrollIntoView() doesn't work
    const me = scrollerRef.current.querySelector('.me') as HTMLElement;
    const bottom = me.offsetTop + me.offsetHeight;

    if (bottom > scrollerRef.current.offsetHeight) {
      scrollerRef.current.scrollTop = bottom - scrollerRef.current.offsetHeight;
    }
  };

  React.useEffect(() => {
    updateScrollPosition();
  } , [visible]);

  return (
    <div className="section profile-background-color-accent" ref={scrollerRef}>
      <div className="content">
        <div className="icon-back" onClick={back}>
          <IconBack/>
        </div>

        <div className="title">
          <FormattedMessage id="appMenu.profileSwitch.heading.text"/>
        </div>

        {profiles.map(p => (
          <div
            key={p.data.id}
            className={cx('avatar', { me: p.data.id === activeProfile.data.id })}
            onClick={(e) => {
              onClickAvatar(e, p.data, selectProfile);
              closeMenu();
            }}
          >
            {
              p.data.id === activeProfile.data.id ? (
                <SelectedProfileAvatar
                  profile={p}
                  altText="appMenu.profileSwitch.me.button.hint"
                  size={48}
                  isMainProfile={p.data.id === activeProfileId}
                />
              ) : (
                <UnselectedProfileAvatar
                  profile={p}
                  altText="appMenu.profileSwitch.me.button.hint"
                  size={48}
                  imgClassName={null}
                  isMainProfile={p.data.id === activeProfileId}
                />
              )
            }

            <div>
              {p.data.id !== mainProfileId ? p.greetingName() : <FormattedMessage id="appMenu.profileSwitch.me.button.label" />}
            </div>
          </div>
        ))}

        <div className={cx('add-profile', 'profile-border-top-color-highlight-25')}>
          <div className="click-wrapper" onClick={() => { navigateReplace(R.createDependentProfile()); closeMenu(); }}>
            <IconAdd fillClassName="fill-color-transparent" strokeClassName="stroke-color-white" size="24" />

            <div>
              <FormattedMessage id="appMenu.addProfile.button.label" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const navItems = [
  { icon: <IconHealthInfo.Medium color="white" />, label: 'appMenu.health.button.label', href: R.home(), onClick: () => { navigateReplace(R.home()); } },
  { icon: <IconProfile.Medium color="white" />, label: 'appMenu.profile.button.label', href: R.profile(), onClick: () => { navigateReplace(R.profile()); } },
  { icon: <Icons.IconProvidersContacts.Medium color="white" />, label: 'appMenu.contacts.button.label', href: R.contactList(), onClick: () => { navigateReplace(R.contactList()); } },
  { icon: <IconShare.Medium color="white"/>, label: 'appMenu.share.button.label', href: R.share(), onClick: () => { TrackEvent.shareCards.shareCardSelectedSideNav(); navigateReplace(R.share()); } },
  { icon: <IconGroups color="white" size="32"/>, label: 'appMenu.groups.button.label', href: R.groups(), onClick: () => { navigateReplace(R.groups()); } },
  { icon: <IconDataConnections color="gsLightest" />, label: 'appMenu.dataSources.button.label', href: R.dataConnections(), onClick: () => { navigateReplace(R.dataConnections()); } },
  { icon: <IconAccess />, label: 'appMenu.access.button.label', href: R.profileAccess(), onClick: () => { navigateReplace(R.profileAccess()); } },
];

function mapStateToProps(state) {
  if (!state.data) return {};

  const profile = getActiveProfile(state);
  const activeProfileId = getMainProfileId(state);

  const profiles = getProfiles(state).sort((a, b) => {
    if (a.data.id === activeProfileId) return -1;
    if (b.data.id === activeProfileId) return 1;

    if (DateUtil.isBefore(a.getDateOfBirth(), b.getDateOfBirth())) return -1;
    return 1;
  });

  const colorTheme = colorThemeSelector.getCurrentColorTheme(state);

  return {
    activeProfile: profile,
    profileList: profiles.map(p => p.data),
    profiles,
    colorTheme,
    showUpgradeButton: sessionSelectors.isUpgradeAvailable(state),
    appMenuStateName: uiSelectors.currentSection(state),
    isSectionVisible: uiSelectors.isSectionVisible(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {};
}
